import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./DepartmentSidebar.css";
import { useAccess, canSeePath } from "../access.jsx";

const SvgIcon = ({ name }) => {
  const paths = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
    care: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></>,
    quality: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></>,
    hr: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
    finance: <><circle cx="12" cy="12" r="9"/><path d="M16 8h-6.5a2.5 2.5 0 0 0 0 5H14a2.5 2.5 0 0 1 0 5H7M12 5v14"/></>,
    growth: <><path d="M3 3v18h18"/><path d="m7 16 4-5 4 3 5-7"/></>,
    admin: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06-2.12 2.12-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V20h-3v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06-2.12-2.12.06-.06A1.65 1.65 0 0 0 7 15a1.65 1.65 0 0 0-1.51-1H5v-3h.49A1.65 1.65 0 0 0 7 10a1.65 1.65 0 0 0-.33-1.82l-.06-.06L8.73 6l.06.06A1.65 1.65 0 0 0 10.61 6a1.65 1.65 0 0 0 1-1.51V4h3v.49a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06 2.12 2.12-.06.06A1.65 1.65 0 0 0 19 10a1.65 1.65 0 0 0 1.51 1H21v3h-.49A1.65 1.65 0 0 0 19.4 15z"/></>,
    portal: <><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 9h8M8 13h5"/></>,
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name] || paths.dashboard}</svg>;
};

const adminDepartments = [
  { id: "care", label: "Care Operations", icon: "care", to: "/admin/care", items: [["Clients", "/admin/clients"], ["Caregivers", "/admin/caregivers"], ["Scheduling", "/admin/scheduling"], ["Live Operations", "/admin/operations"], ["EVV Monitor", "/admin/evv"], ["Communications", "/admin/communications"]] },
  { id: "quality", label: "Quality & Compliance", icon: "quality", to: "/admin/quality-hub", items: [["Quality Center", "/admin/quality"], ["Inspector Access", "/admin/inspector-access"], ["Progress Notes", "/admin/progress-notes"], ["Visit Verification", "/admin/visit-verification"], ["Agency Intelligence", "/admin/intelligence"], ["Dementia Training", "/admin/dhhs/dementia-training"], ["TB Testing", "/admin/dhhs/tb-testing"], ["QA Meetings", "/admin/dhhs/qa-meetings"], ["PCA Supervision", "/admin/dhhs/pca-supervision"]] },
  { id: "workforce", label: "Workforce & HR", icon: "hr", to: "/admin/hr", items: [["Employees", "/admin/employees"], ["Recruiting", "/admin/recruiting"], ["New Hire Packets", "/admin/onboarding"], ["HR Management", "/admin/hr"], ["Training Dashboard", "/admin/training"], ["Compliance Dashboard", "/admin/dashboard"], ["Payroll", "/admin/payroll"], ["Mileage", "/admin/mileage"]] },
  { id: "finance", label: "Finance & Revenue", icon: "finance", to: "/admin/finance", items: [["Accounting", "/admin/accounting"], ["Billing Center", "/admin/billing"], ["Reports", "/admin/reports"]] },
  { id: "growth", label: "Growth & Intelligence", icon: "growth", to: "/admin/growth", items: [["CRM & Referrals", "/admin/crm"], ["Executive BI", "/admin/executive"]] },
  { id: "administration", label: "Administration", icon: "admin", to: "/admin/administration", items: [["Users & Access", "/admin/users"], ["Multi-Branch", "/admin/enterprise"], ["Data Migration", "/admin/data-migration"]] },
];

const portalDepartments = [
  { id: "portal", label: "My Caregiver Portal", icon: "portal", to: "/portal", items: [["New Hire Packet", "/portal/onboarding"], ["My Schedule", "/portal/schedule"], ["Mobile Portal", "/portal/mobile"], ["EVV / Clock In", "/portal/evv"], ["Messages", "/portal/communications"], ["Compliance", "/portal/compliance"], ["Training", "/portal/training"], ["Mileage", "/portal/mileage"], ["Timesheets", "/portal/timesheets"]] },
];

const familyDepartments = [
  { id: "family", label: "Family Portal", icon: "portal", to: "/family" },
];

// Sub-items are not rendered here; each department's landing page shows
// them as cards. They are kept so the heading stays highlighted while on
// any page that belongs to the department.
const matchesPath = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

function Department({ department, onNavigate }) {
  const { pathname } = useLocation();
  const ownsPath = (department.items || []).some(([, to]) => matchesPath(pathname, to));
  return (
    <div className={`sidebar-department department-${department.id}`}>
      {department.to ? (
        <NavLink to={department.to} onClick={onNavigate} className={({ isActive }) => `department-heading ${isActive || ownsPath ? "is-active" : ""}`}>
          <span className="department-heading-icon"><SvgIcon name={department.icon} /></span>
          <span>{department.label}</span>
        </NavLink>
      ) : (
        <div className="department-heading department-heading-static">
          <span className="department-heading-icon"><SvgIcon name={department.icon} /></span>
          <span>{department.label}</span>
        </div>
      )}
    </div>
  );
}

export default function DepartmentSidebar({ user, onLogout, isOpen, onNavigate }) {
  const role = user?.role;
  const isStaff = role === "staff";
  const access = useAccess();
  const isAdmin = role === "admin" || isStaff;
  const isFamily = role === "family";
  const staffDepartments = isStaff ? adminDepartments
    .map((d) => ({ ...d, to: canSeePath(access, d.to) ? d.to : undefined, items: d.items.filter(([, to]) => canSeePath(access, to)) }))
    .filter((d) => d.items.length) : null;
  if (isStaff && access.caregiver && staffDepartments) staffDepartments.unshift({ ...portalDepartments[0], label: "My Caregiver Work", items: [["New Hire Packet", "/portal/onboarding"], ...portalDepartments[0].items.filter(([l]) => l !== "New Hire Packet")] });
  else if (isStaff && staffDepartments) staffDepartments.unshift({ id: "portal", label: "Me", icon: "portal", items: [["My New Hire Packet", "/portal/onboarding"]] });
  const departments = isStaff ? staffDepartments : isAdmin ? adminDepartments : isFamily ? familyDepartments : portalDepartments;
  const userLabel = useMemo(() => user?.display_name || user?.name || user?.username || "User", [user]);

  return (
    <aside className={`department-sidebar ${isOpen ? "mobile-open" : ""}`}>
      <div className="department-brand">
        <img src="/logo.png" alt="JyBek HomeCare Services" onError={(event) => { event.currentTarget.style.display = "none"; }} />
        <div className="department-brand-fallback">JYBEK<br/><small>HOMECARE SERVICES</small></div>
        <div className="department-platform-name">One-stop agency platform</div>
        <div className="department-user-line">{userLabel} · {isStaff ? (access.caregiver ? "office staff · caregiver" : "office staff") : isAdmin ? "admin" : isFamily ? "family" : "caregiver"}</div>
      </div>

      <nav className="department-nav" aria-label="Department navigation">
        <NavLink to="/" end onClick={onNavigate} className={({ isActive }) => `department-dashboard ${isActive ? "active" : ""}`}>
          <span className="department-heading-icon"><SvgIcon name="dashboard" /></span><span>Dashboard</span>
        </NavLink>
        <div className="department-divider"><span>Departments</span></div>
        {departments.map((department) => (
          <Department key={department.id} department={department} onNavigate={onNavigate} />
        ))}
      </nav>

      <button className="department-logout" type="button" onClick={onLogout}>↪ <span>Log out</span></button>
    </aside>
  );
}
